import * as React from 'react';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';

import { VALID_BREAKPOINTS_WITH_BASE } from '../Theme/common';
import Box, { IBoxProps } from '../Layout/Box';
import { VALID_DIMENSION_DATA } from '../Utils';

import DescriptionGroup from './DescriptionGroup';
import { DescriptionListContext, IDescriptionGroupContext, IDescriptionListContext } from './DescriptionListContext';
import { DescriptionDetail, DescriptionTerm } from './DescriptionStyles';

const ALIGNMENT_MAP_NO_STACK = {
  left: 'flex-start',
  right: 'space-between',
  center: ''
};

const ALIGNMENT_MAP_STACK = {
  left: 'flex-start',
  right: 'flex-end',
  center: 'center'
};

type VALID_ALIGN_MAP_NO_STACK = { [key in VALID_BREAKPOINTS_WITH_BASE]?: 'right' | 'left' };
type VALID_ALIGN_MAP_STACK = { [key in VALID_BREAKPOINTS_WITH_BASE]?: 'right' | 'left' | 'center' };

export interface IDescriptionListCommonProps {
  fixTermWidth?: VALID_DIMENSION_DATA;
  noMobileStack?: boolean; // mobile stacking happens by default
}

interface IDescriptionListProps extends IDescriptionListCommonProps, IBoxProps {
  title: string;
}

interface IStacked extends IDescriptionListProps {
  stacked: true;
  align?: 'right' | 'left' | 'center' | VALID_ALIGN_MAP_STACK;
  contentOrderSwitch?: boolean;
}

interface INotStacked extends IDescriptionListProps {
  align?: 'right' | 'left' | VALID_ALIGN_MAP_NO_STACK;
  stacked?: false;
}

// only allows content to change order once stack is true
type DescriptionListProp = IStacked | INotStacked;

const getAlignment = (
  alignment: 'right' | 'left' | 'center' | VALID_ALIGN_MAP_STACK | VALID_ALIGN_MAP_NO_STACK,
  stacked?: boolean
) => {
  const alignmentMap = !stacked ? ALIGNMENT_MAP_NO_STACK : ALIGNMENT_MAP_STACK;

  if (typeof alignment === 'string') {
    return alignmentMap[alignment];
  } else {
    return mapValues(alignment, val => alignmentMap[val as 'right' | 'left' | 'center']);
  }
};

export const DescriptionList: React.FC<DescriptionListProp> &
{ detail: React.FC<IDescriptionGroupContext>; group: React.FC<IDescriptionGroupContext>; term: React.FC<IDescriptionGroupContext> } =
({ align, fixTermWidth, stacked, title, noMobileStack, width, children, ...rest }) => {
  const directionDefault: IDescriptionListContext['direction'] = {
    base: noMobileStack ? 'row' : 'column',
    small: 'row'
  };

  const directionStacked: IDescriptionListContext['direction'] = {
    base: noMobileStack ? 'row' : 'column',
    small: 'column'
  };

  const direction = stacked ? directionStacked : directionDefault;
  const alignment = align && getAlignment(align, stacked);
  const contentOrderSwitch: boolean | undefined = (rest as IStacked).contentOrderSwitch ? (rest as IStacked).contentOrderSwitch : undefined;
  const pickedBoxProps: IBoxProps = omit(rest , 'contentOrderSwitch');

  return (
    <DescriptionListContext.Provider
      value={{
        direction,
        noMobileStack,
        align: alignment,
        stacked,
        contentOrderSwitch,
        fixTermWidth,
      }}
    >
      <Box
        direction='column'
        title={title}
        {...pickedBoxProps}
        // below props should remain under pickedBoxProps so it can not be overridden
        forwardedAs='dl'
      >
        {children}
      </Box>
    </DescriptionListContext.Provider>
  );
};

DescriptionList.term = DescriptionTerm;
DescriptionList.detail = DescriptionDetail;
DescriptionList.group = DescriptionGroup;

export default DescriptionList;
